<template>
  <div class="right-sidebar-container" :style="sidebarStyle" :class="{ 'collapsed': collapsed, 'mobile-open': mobileOpen }">
    <!-- 侧边栏头部 - 包含收缩按钮 -->
    <div class="sidebar-header">
      <div class="header-content" v-if="!collapsed">
        <h3 class="sidebar-title">菜单导航</h3>
      </div>
      <div class="collapse-btn" @click="handleToggleCollapse">
        <i :class="collapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
      </div>
    </div>

    <!-- 动态菜单列表 - 只有展开时显示 -->
    <div class="sidebar-content" v-if="!collapsed">
      <div class="menu-list">
        <!-- 工作台（固定） -->
        <div
            class="menu-item"
            :class="{ 'active': activeMenu === 'dashboard' }"
            @click="navigateTo('/home')"
        >
          <i class="el-icon-s-home menu-icon"></i>
          <span class="menu-title">工作台</span>
        </div>

        <!-- 一级菜单（系统模块） -->
        <div
            v-for="system in routersData"
            :key="system.menuId"
            class="menu-item directory-item"
            :class="{
            'active': activeMenu === system.menuId,
            'expanded': expandedMenus.includes(system.menuId)
          }"
        >
          <!-- 目录标题 - 点击展开/收起 -->
          <div class="directory-header" @click="toggleDirectory(system.menuId)">
            <i :class="'el-icon-' + (system.meta?.icon || 'menu') + ' menu-icon'"></i>
            <span class="menu-title">{{ system.meta?.title || system.name }}</span>
            <i class="el-icon-arrow-down expand-icon" :class="{ 'expanded': expandedMenus.includes(system.menuId) }"></i>
          </div>

          <!-- 子菜单列表（功能菜单） -->
          <div v-if="expandedMenus.includes(system.menuId)" class="submenu-list">
            <div
                v-for="submenu in system.children || []"
                :key="submenu.menuId"
                class="submenu-item"
                :class="{ 'active': activeSubMenu === submenu.menuId }"
                @click="handleSubMenuClick(submenu)"
            >
              <i :class="'el-icon-' + (submenu.meta?.icon || 'document')" class="submenu-icon"></i>
              <span class="submenu-title">{{ submenu.meta?.title || submenu.name }}</span>
            </div>

            <!-- 空状态 -->
            <div v-if="!system.children || system.children.length === 0" class="empty-submenu">
              暂无子菜单
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 移动端遮罩层 -->
    <div v-if="isMobile && mobileOpen" class="mobile-mask" @click="closeMobileSidebar"></div>
  </div>
</template>

<script>
import { getRouters } from "@/api/system/systemMenu.js";
import {EventBus} from "@/untils/eventBus";

export default {
  name: 'RightSidebar',
  props: {
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeMenu: 'dashboard',
      activeSubMenu: null,
      mobileOpen: false,
      isMobile: false,
      internalCollapsed: false,

      // 路由数据
      routersData: [],

      // 展开的目录ID数组
      expandedMenus: []
    };
  },
  computed: {
    sidebarStyle() {
      return {
        backgroundColor: '#ffffff',
        color: '#333333',
        borderLeft: '1px solid #e4e7ed'
      };
    },

    actualCollapsed() {
      return this.internalCollapsed || this.collapsed;
    }
  },
  watch: {
    collapsed: {
      immediate: true,
      handler(newVal) {
        this.internalCollapsed = newVal;
      }
    },

    actualCollapsed(newVal) {
      if (this.isMobile && !newVal) {
        this.mobileOpen = true;
      } else if (this.isMobile && newVal) {
        this.mobileOpen = false;
      }
    }
  },
  created() {
    this.getRouters();
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
    this.internalCollapsed = this.collapsed;

    EventBus.$on('menu-updated', this.handleMenuUpdate);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
    EventBus.$off('menu-updated', this.handleMenuUpdate);
  },
  methods: {
    handleMenuUpdate() {
      console.log('菜单已更新，重新加载侧边栏');
      this.getRouters(); // 重新获取路由数据
    },
    // 获取路由数据
    async getRouters() {
      try {
        const res = await getRouters();
        if (res.code === 200) {
          this.routersData = res.data || [];
          console.log('路由数据:', this.routersData);
        }
      } catch (error) {
        console.error('获取路由失败:', error);
      }
    },

    // 切换目录展开/收起
    toggleDirectory(menuId) {
      if (this.expandedMenus.includes(menuId)) {
        // 如果已经展开，则收起
        this.expandedMenus = this.expandedMenus.filter(id => id !== menuId);
      } else {
        // 否则展开
        this.expandedMenus.push(menuId);
      }
    },

    // 检查屏幕尺寸
    checkScreenSize() {
      this.isMobile = window.innerWidth < 768;
      if (this.isMobile && !this.actualCollapsed) {
        this.mobileOpen = true;
      } else {
        this.mobileOpen = false;
      }
    },

    // 切换侧边栏收缩状态
    handleToggleCollapse() {
      if (this.isMobile) {
        this.mobileOpen = !this.mobileOpen;
        this.internalCollapsed = !this.mobileOpen;
      } else {
        this.internalCollapsed = !this.internalCollapsed;
      }
      this.$emit('toggle-collapse', this.internalCollapsed);
    },

    // 关闭移动端侧边栏
    closeMobileSidebar() {
      this.mobileOpen = false;
      this.internalCollapsed = true;
      this.$emit('toggle-collapse', true);
    },

    // 子菜单点击处理
    handleSubMenuClick(submenu) {
      this.activeSubMenu = submenu.menuId;
      this.activeMenu = null; // 清除目录激活状态

      // 如果有路由路径，直接跳转
      if (submenu.path) {
        const routePath = submenu.path.startsWith('/') ? submenu.path : '/' + submenu.path;
        this.$router.push(routePath).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error('路由跳转失败:', err);
          }
        });
      }

      if (this.isMobile) {
        this.closeMobileSidebar();
      }
    },

    // 路由跳转
    navigateTo(path) {
      if (path) {
        this.activeMenu = 'dashboard';
        this.activeSubMenu = null;
        this.$router.push(path);
        if (this.isMobile) {
          this.closeMobileSidebar();
        }
      }
    }
  }
}
</script>

<style lang="less" scoped>
/* 样式保持不变 */
.right-sidebar-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid #e4e7ed;
  transition: width 0.3s ease;
  overflow: hidden;
  position: relative;

  &.collapsed {
    width: 40px; // 收缩时只保留按钮宽度

    .sidebar-header {
      padding: 16px 0;
      justify-content: center;

      .header-content {
        display: none;
      }

      .collapse-btn {
        margin: 0;
      }
    }

    .sidebar-content {
      display: none; // 完全隐藏内容
    }
  }

  &.mobile-open {
    transform: translateX(0);
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
    z-index: 1001;
  }
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e4e7ed;
  background: #fafbfc;
  flex-shrink: 0;
  height: 60px;
  box-sizing: border-box;

  .header-content {
    flex: 1;
    overflow: hidden;
  }

  .sidebar-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .collapse-btn {
    width: 32px;
    height: 32px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;

    &:hover {
      background: #f0f2f5;
      transform: scale(1.1);
    }

    i {
      font-size: 18px;
      color: #606266;
    }
  }
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }
}

.menu-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.menu-item {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f7fa;
  }

  &.active {
    background: #ecf5ff;
    border-left: 3px solid #409EFF;

    .menu-icon {
      color: #409EFF;
    }

    .menu-title {
      color: #409EFF;
      font-weight: 500;
    }
  }
}

.directory-header {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f7fa;
  }

  .menu-icon {
    font-size: 18px;
    color: #606266;
    margin-right: 12px;
    transition: all 0.2s ease;
    width: 20px;
    text-align: center;
  }

  .menu-title {
    flex: 1;
    font-size: 14px;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .expand-icon {
    font-size: 14px;
    color: #909399;
    transition: transform 0.3s ease;
    margin-left: 8px;

    &.expanded {
      transform: rotate(180deg);
    }
  }
}

.submenu-list {
  background: #f8fafc;
  padding: 4px 0;
}

.submenu-item {
  display: flex;
  align-items: center;
  padding: 8px 20px 8px 52px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;

  &:hover {
    background: #e6f7ff;

    .submenu-icon {
      color: #409EFF;
    }

    .submenu-title {
      color: #409EFF;
    }
  }

  &.active {
    background: #ecf5ff;

    .submenu-icon {
      color: #409EFF;
    }

    .submenu-title {
      color: #409EFF;
      font-weight: 500;
    }
  }

  .submenu-icon {
    font-size: 14px;
    color: #606266;
    margin-right: 8px;
    width: 16px;
    text-align: center;
  }

  .submenu-title {
    color: #5a6a7e;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.empty-submenu {
  padding: 8px 20px 8px 52px;
  color: #999;
  font-size: 12px;
  text-align: center;
}

/* 工作台菜单项样式 */
.menu-item:not(.directory-item) {
  flex-direction: row;
  align-items: center;
  padding: 10px 20px;

  .menu-icon {
    font-size: 18px;
    color: #606266;
    margin-right: 12px;
    width: 20px;
    text-align: center;
  }

  .menu-title {
    flex: 1;
    font-size: 14px;
    color: #333;
  }
}

.mobile-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .right-sidebar-container {
    position: fixed;
    top: 60px;
    right: 0;
    height: calc(100vh - 60px);
    transform: translateX(100%);
    z-index: 1001;
    box-shadow: none;
    width: 100% !important;

    &.mobile-open {
      transform: translateX(0);
      animation: slideIn 0.3s ease;
    }

    &.collapsed {
      width: 40px !important;

      &.mobile-open {
        transform: translateX(0);
        width: 40px !important;
      }
    }
  }

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
}
</style>